import React, { useState } from 'react';
import { Plus, Trash2, Flame, CalendarClock } from 'lucide-react';
import { api, timeAgo } from '../api';
import { toast } from 'sonner';

const TYPE_LABELS = {
  'followup': { label: 'Follow-up', cls: 'bg-pink-100 text-pink-700' },
  'waiting-response': { label: 'Waiting', cls: 'bg-amber-100 text-amber-700' },
  'call-back': { label: 'Call back', cls: 'bg-sky-100 text-sky-700' },
};

const dueLabel = (dueAt) => {
  const diff = new Date(dueAt).getTime() - Date.now();
  const abs = Math.abs(diff);
  const fmt = abs < 3600000 ? `${Math.max(1, Math.round(abs / 60000))}m` : abs < 86400000 ? `${Math.round(abs / 3600000)}h` : `${Math.round(abs / 86400000)}d`;
  return diff < 0 ? { text: `overdue ${fmt}`, overdue: true } : { text: `due in ${fmt}`, overdue: false };
};

export default function RemindersModule({ agent, reminders, refresh, sendActivity }) {
  const [text, setText] = useState('');
  const [rtype, setRtype] = useState('followup');
  const [urgent, setUrgent] = useState(false);
  const [dueAt, setDueAt] = useState('');

  const add = async () => {
    if (!text.trim()) return;
    await api.post('/reminders', {
      text, rtype, priority: urgent ? 'urgent' : 'normal',
      agent_id: agent.id, agent_name: agent.name,
      due_at: dueAt || null,
    });
    toast.success(dueAt ? 'Reminder pinned with due date!' : 'Reminder pinned!');
    sendActivity(`Pinned reminder: ${text.slice(0, 40)}`);
    setText(''); setUrgent(false); setDueAt('');
    refresh();
  };

  const toggle = async (r) => {
    await api.patch(`/reminders/${r.id}?done=${!r.done}`);
    if (!r.done) sendActivity(`Completed reminder: ${r.text.slice(0, 40)}`);
    refresh();
  };

  const remove = async (r) => {
    await api.delete(`/reminders/${r.id}`);
    refresh();
  };

  const sortDue = (a, b) => {
    if (a.due_at && b.due_at) return new Date(a.due_at) - new Date(b.due_at);
    if (a.due_at) return -1;
    if (b.due_at) return 1;
    return 0;
  };
  const pending = reminders.filter((r) => !r.done).sort(sortDue);
  const done = reminders.filter((r) => r.done);
  const overdueCount = pending.filter((r) => r.due_at && new Date(r.due_at) < new Date()).length;

  return (
    <div data-testid="reminders-widget" className="space-y-2.5">
      <div className="flex gap-1.5">
        <input
          data-testid="reminder-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="e.g. Lead waiting for response…"
          className="flex-1 bg-white/80 border border-pink-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-pink-400"
        />
        <button data-testid="reminder-add-button" onClick={add}
          className="bg-pink-700 text-white rounded-lg px-2.5 hover:bg-pink-800 transition-colors"><Plus size={14} /></button>
      </div>
      <div className="flex gap-1.5 items-center flex-wrap">
        <select data-testid="reminder-type-select" value={rtype} onChange={(e) => setRtype(e.target.value)}
          className="bg-white/80 border border-pink-300 rounded-lg px-2 py-1 text-[11px] focus:outline-none">
          <option value="followup">Follow-up</option>
          <option value="waiting-response">Waiting for response</option>
          <option value="call-back">Call back</option>
        </select>
        <button data-testid="reminder-urgent-toggle" onClick={() => setUrgent(!urgent)}
          className={`flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-lg border transition-colors ${urgent ? 'bg-red-500 text-white border-red-500' : 'bg-white/70 text-red-500 border-red-300 hover:bg-red-50'}`}>
          <Flame size={11} /> Urgent
        </button>
      </div>
      <div className="flex gap-1.5 items-center">
        <CalendarClock size={13} className="text-pink-700/60 shrink-0" />
        <input
          data-testid="reminder-due-input"
          type="datetime-local"
          value={dueAt}
          onChange={(e) => setDueAt(e.target.value)}
          className="flex-1 bg-white/80 border border-pink-300 rounded-lg px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-pink-400 text-slate-600"
        />
        {dueAt && <button onClick={() => setDueAt('')} className="text-[10px] text-pink-700/60 hover:text-pink-900 font-bold">clear</button>}
      </div>

      {overdueCount > 0 && (
        <div data-testid="overdue-banner" className="bg-red-500 text-white rounded-lg px-2.5 py-1.5 text-[11px] font-bold flex items-center gap-1.5 rotate-[-0.5deg] sticky-shadow">
          <Flame size={12} /> {overdueCount} follow-up{overdueCount > 1 ? 's are' : ' is'} overdue — jump on it!
        </div>
      )}

      <div className="space-y-1.5 max-h-[330px] overflow-y-auto scroll-thin pr-1" data-testid="reminders-list">
        {pending.length === 0 && <p className="font-hand text-lg text-pink-800/60 text-center py-4">All clear! Nothing pending 🎈</p>}
        {pending.map((r) => {
          const due = r.due_at ? dueLabel(r.due_at) : null;
          return (
          <div key={r.id} data-testid={`reminder-item-${r.id}`}
            className={`flex items-start gap-2 rounded-lg px-2.5 py-2 border ${due?.overdue ? 'bg-red-50 border-red-400' : r.priority === 'urgent' ? 'bg-white/85 border-red-300' : 'bg-white/85 border-pink-200'}`}>
            <input type="checkbox" data-testid={`reminder-check-${r.id}`} checked={false} onChange={() => toggle(r)}
              className="mt-0.5 accent-pink-600 cursor-pointer" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-800 leading-snug">{r.text}</p>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${TYPE_LABELS[r.rtype]?.cls || ''}`}>{TYPE_LABELS[r.rtype]?.label || r.rtype}</span>
                {r.priority === 'urgent' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white flex items-center gap-0.5"><Flame size={8} />URGENT</span>}
                {due && (
                  <span data-testid={`reminder-due-badge-${r.id}`} className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${due.overdue ? 'bg-red-600 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                    <CalendarClock size={8} /> {due.text}
                  </span>
                )}
                <span className="text-[9px] text-slate-400">{timeAgo(r.created_at)}</span>
              </div>
            </div>
            <button onClick={() => remove(r)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={12} /></button>
          </div>
          );
        })}
        {done.length > 0 && (
          <>
            <p className="font-hand text-sm text-pink-800/50 pt-1">done ✓</p>
            {done.map((r) => (
              <div key={r.id} className="flex items-start gap-2 rounded-lg px-2.5 py-1.5 border border-pink-100 bg-white/50 opacity-60">
                <input type="checkbox" checked onChange={() => toggle(r)} className="mt-0.5 accent-pink-600 cursor-pointer" />
                <p className="text-xs text-slate-500 line-through flex-1">{r.text}</p>
                <button onClick={() => remove(r)} className="text-slate-300 hover:text-red-500"><Trash2 size={12} /></button>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
